import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Package, Check, AlertTriangle, Link2 } from "lucide-react";
import api from "../../lib/api";
import { useGuestGate } from "../../lib/useGuestGate";
import { BRAND_COLORS, withAlpha } from "../../lib/bookingStatus";
import { deDate } from "../../lib/dateFormat";
import { deCurrency } from "../../lib/numberFormat";
import {
  absenceCoveredByChain,
  coverageWarningMessage,
  packageEndDate,
  chainStart,
  laterOverlappingWindows,
  recommendedPackageFromStart,
} from "../../lib/servicePackageLimits";
import CustomerPinPromptDialog from "./CustomerPinPromptDialog";
import { useOnline } from "../../lib/offline";

// Zentraler Paket-Katalog der Kundenansicht — Single Source of Truth für beide
// Consumer (Service-Paket-Seite + Assistent „Was steht an?").
export const PACKAGES = [
  {
    id: "1",
    title: "Service-Paket 1",
    title_en: "Service Package 1",
    main: "Immobilienbetreuung für mehr als drei Monate",
    main_en: "Property care for more than three months",
    note: "Paket fortlaufend bis auf Widerruf",
    note_en: "Ongoing until cancelled",
  },
  {
    id: "2",
    title: "Service-Paket 2",
    title_en: "Service Package 2",
    main: "Immobilienbetreuung bis zu drei Monaten",
    main_en: "Property care for up to three months",
    note: "Mindestlaufzeit 1 Monat. Endet automatisch.",
    note_en: "Minimum term 1 month. Ends automatically.",
  },
  {
    id: "3",
    title: "Service-Paket 3",
    title_en: "Service Package 3",
    main: "Immobilienbetreuung für zwei Wochen",
    main_en: "Property care for two weeks",
    note: "endet automatisch",
    note_en: "Ends automatically",
  },
];

/**
 * Modell A · Wiederverwendbare Paketauswahl + PIN-Buchung.
 *
 * Wird an ZWEI Stellen inline gerendert:
 *  - `CustomerServicePackages` (eigene Seite)
 *  - `CustomerArrival` (Assistent — direkt unter dem Zeitraum, wenn die
 *    Abwesenheit noch nicht durch die Paket-Kette gedeckt ist).
 *
 * Ketten-bewusstes Startdatum (spiegelt das Backend):
 *  - „follow": es läuft bereits ein endliches Fenster → Start ist FIX
 *    (letztes Ende + 1). Kein Datumsfeld, nur die Anschluss-Anzeige.
 *  - „first":  kein aktives Fenster → Kunde wählt das Startdatum.
 *  - „open":   fortlaufendes Paket 1 aktiv → keine weitere Buchung nötig.
 *
 * Preise + PIN-Status lädt die Komponente selbst. `bookings` (Paketbuchungen)
 * und `absences` kommen vom Consumer, damit ein `onBooked`-Reload den Zustand
 * überall synchron hält.
 */
export default function ServicePackageBooker({
  bookings = [],
  absences = [],
  propertyId,
  propertyName = "",
  multi = false,
  de = true,
  presetPackageId = null,
  defaultStart = "",
  requiredCoverage = null, // { from, to } — Pflichtdeckung (Assistent)
  onBooked,
  testidPrefix = "service-packages",
}) {
  const gate = useGuestGate();
  const online = useOnline();
  const todayStr = new Date().toISOString().slice(0, 10);

  const [selectedId, setSelectedId] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [replaceOverlap, setReplaceOverlap] = useState(false);
  const [pinIsSet, setPinIsSet] = useState(null);
  const [pinMode, setPinMode] = useState(null); // "confirm" | "setup" | null
  const [submitting, setSubmitting] = useState(false);
  const [prices, setPrices] = useState({ prices: {}, currency: "EUR" });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/me/customer-pin/status");
        setPinIsSet(!!data.is_set);
      } catch (e) { setPinIsSet(false); }
    })();
    (async () => {
      try {
        const { data } = await api.get("/service-packages/prices");
        setPrices(data || { prices: {}, currency: "EUR" });
      } catch (e) { /* Preis optional */ }
    })();
  }, []);

  // Beabsichtigter Abwesenheitsbeginn (Assistent: Pflichtdeckung; sonst Vorgabe).
  const absenceFrom = requiredCoverage?.from || defaultStart || null;
  // Anschluss-Status der Kette für dieses Objekt (lücken-bewusst).
  const chainInfo = useMemo(
    () => chainStart(bookings, propertyId, todayStr, absenceFrom),
    [bookings, propertyId, todayStr, absenceFrom]
  );
  const isFollow = chainInfo.mode === "follow";
  const isOpen = chainInfo.mode === "open";

  const initialStartFor = () => {
    if (isFollow) return "";
    return defaultStart && defaultStart >= todayStr ? defaultStart : "";
  };

  // Objektwechsel setzt die laufende Auswahl zurück. Auch den Preset-Merker
  // zurücksetzen, damit eine Empfehlung/Deep-Link nach dem (verzögerten) Laden
  // des Objekts erneut greift (propertyId wechselt oft von "" auf die echte ID).
  const lastPreset = useRef(null);
  useEffect(() => {
    setSelectedId(null);
    setStartDate("");
    setConfirmChecked(false);
    setReplaceOverlap(false);
    lastPreset.current = null;
  }, [propertyId]);

  // Preset (Deep-Link / Empfehlung) vorwählen, wenn er sich ändert.
  useEffect(() => {
    if (!presetPackageId || presetPackageId === lastPreset.current) return;
    if (!PACKAGES.some((p) => p.id === presetPackageId)) return;
    lastPreset.current = presetPackageId;
    setSelectedId(presetPackageId);
    setStartDate(initialStartFor());
    setConfirmChecked(false);
  }, [presetPackageId, propertyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const effectiveStart = isFollow ? chainInfo.start : startDate;
  const selectedPkg = PACKAGES.find((p) => p.id === selectedId);

  const priceLabel = (id) => {
    const v = prices?.prices?.[id];
    if (v == null) return de ? "Preis auf Anfrage" : "Price on request";
    return de
      ? `${deCurrency(v, prices.currency || "EUR")} pro Woche`
      : `${deCurrency(v, prices.currency || "EUR")} per week`;
  };

  const pkgTitle = (p) => (p ? (de ? p.title : p.title_en) : "");
  const pkgMain = (p) => (p ? (de ? p.main : p.main_en) : "");
  const pkgNote = (p) => (p ? (de ? p.note : p.note_en) : "");

  const selectPackage = (id) => {
    setSelectedId((cur) => (cur === id ? null : id));
    setStartDate(initialStartFor());
    setConfirmChecked(false);
    setReplaceOverlap(false);
  };

  // Simulierte Kette NACH Buchung des gewählten Pakets ab `effectiveStart`.
  const simulated = useMemo(() => {
    if (!selectedId || !propertyId || !effectiveStart) return null;
    const end = packageEndDate(selectedId, effectiveStart); // null = Paket 1 offen
    return [...(bookings || []), { property_id: propertyId, start_date: effectiveStart, end_date: end }];
  }, [selectedId, propertyId, effectiveStart, bookings]);

  // Buchungssperre AUSSCHLIESSLICH für die gerade angelegte Abwesenheit
  // (requiredCoverage). Andere, bereits gespeicherte Abwesenheiten des Objekts
  // blockieren NICHT — dafür gibt es den neutralen Hinweis `otherUncovered`.
  const selectionWarning = useMemo(() => {
    if (!simulated) return null;
    if (requiredCoverage?.from && requiredCoverage?.to
        && !absenceCoveredByChain(simulated, propertyId, requiredCoverage.from, requiredCoverage.to)) {
      return coverageWarningMessage(requiredCoverage.from, requiredCoverage.to, de);
    }
    return null;
  }, [simulated, requiredCoverage, propertyId, de]);

  // Nicht-blockierender, neutraler Hinweis auf ANDERE noch ungedeckte
  // Abwesenheiten DESSELBEN Objekts (streng nach property_id — nie
  // objektübergreifend). Die gerade angelegte Abwesenheit ist ausgenommen.
  const otherUncovered = useMemo(() => {
    return (absences || [])
      .filter((a) => a.property_id === propertyId && a.status !== "cancelled" && (a.to_date || "") >= todayStr
        && !(requiredCoverage && a.from_date === requiredCoverage.from && a.to_date === requiredCoverage.to)
        && !absenceCoveredByChain(bookings, propertyId, a.from_date, a.to_date))
      .sort((a, b) => (a.from_date < b.from_date ? -1 : 1));
  }, [absences, bookings, propertyId, requiredCoverage, todayStr]);

  // Späteres, bereits gebuchtes Fenster, mit dem sich das gewählte Paket
  // (ab effectiveStart) überschneiden würde. Nur im NICHT-follow-Fall relevant
  // (echte Lücke/erstes Paket). Rückgabe steuert Hinweis + Gesamtzeitraum-Vorschlag.
  const laterOverlap = useMemo(() => {
    if (!selectedId || !propertyId || !effectiveStart || isFollow) return [];
    const end = packageEndDate(selectedId, effectiveStart); // null = Paket 1 offen
    return laterOverlappingWindows(bookings, propertyId, effectiveStart, end);
  }, [selectedId, propertyId, effectiveStart, isFollow, bookings]);

  // Kürzestes Paket, das AB effectiveStart den GESAMTEN Zeitraum inkl. des
  // spätesten überschneidenden Fensters abdeckt (ersetzt das spätere Paket).
  const fullRangeSuggestion = useMemo(() => {
    if (!laterOverlap.length || !effectiveStart) return null;
    const lastEnd = laterOverlap
      .map((w) => w.end)
      .reduce((a, b) => (a === null || b === null ? null : b > a ? b : a), laterOverlap[0].end);
    if (lastEnd === null) return "1";
    return recommendedPackageFromStart(effectiveStart, lastEnd) || "1";
  }, [laterOverlap, effectiveStart]);

  const chooseFullRange = () => {
    if (!fullRangeSuggestion) return;
    setSelectedId(fullRangeSuggestion);
    setReplaceOverlap(true);
    setConfirmChecked(false);
  };

  // Überschneidung blockiert, solange der Kunde nicht das Gesamtzeitraum-Paket
  // (replaceOverlap) bestätigt hat.
  const overlapBlocks = laterOverlap.length > 0 && !replaceOverlap;

  const startBooking = () => {
    if (!online) { toast.error(de ? "Diese Aktion benötigt eine Internetverbindung." : "This action requires an internet connection."); return; }
    if (gate.isGuest) { gate.promptUpgrade(); return; }
    if (!selectedId || !confirmChecked || !effectiveStart) return;
    setPinMode(pinIsSet ? "confirm" : "setup");
  };

  const submitBooking = async (pin) => {
    if (!selectedId) return;
    setSubmitting(true);
    try {
      await api.post("/service-packages/book", {
        package_id: selectedId,
        property_id: propertyId || null,
        start_date: effectiveStart,
        confirmed: true,
        replace_overlap: replaceOverlap,
        pin,
      });
      toast.success(de ? "Service-Paket verbindlich gebucht." : "Service package booked.");
      setSelectedId(null);
      setStartDate("");
      setConfirmChecked(false);
      setPinMode(null);
      lastPreset.current = null;
      await onBooked?.();
    } catch (e) {
      if (e?.response?.status === 428) {
        toast.info(de ? "Bitte legen Sie zuerst eine 4-stellige PIN an." : "Please set up a 4-digit PIN first.");
        setPinIsSet(false);
        setPinMode("setup");
      } else {
        throw e;
      }
    } finally {
      setSubmitting(false);
    }
  };

  const onPinSetupSuccess = () => {
    setPinIsSet(true);
    setPinMode("confirm");
  };

  // Fortlaufendes Paket 1 läuft bereits → keine weitere Buchung möglich/nötig.
  if (isOpen) {
    return (
      <div
        className="flex items-start gap-3 border p-4 rounded"
        style={{ background: withAlpha(BRAND_COLORS.gold, 0.10), borderColor: BRAND_COLORS.gold }}
        data-testid={`${testidPrefix}-open-info`}
      >
        <Package className="w-5 h-5 mt-0.5 shrink-0" strokeWidth={1.6} style={{ color: BRAND_COLORS.gold }} />
        <div className="text-[13px] leading-snug text-navy">
          {de
            ? `Für dieses Objekt${multi && propertyName ? ` („${propertyName}“)` : ""} läuft bereits ein fortlaufendes Service-Paket 1 – Ihre Abwesenheiten sind durchgehend abgedeckt. Eine weitere Buchung ist nicht nötig.`
            : `This property${multi && propertyName ? ` (“${propertyName}”)` : ""} already has an ongoing Service Package 1 – your absences are continuously covered. No further booking is needed.`}
        </div>
      </div>
    );
  }

  return (
    <div data-testid={`${testidPrefix}-booker`}>
      {otherUncovered.length > 0 && (
        <div
          className="mb-4 border p-4 rounded"
          style={{ background: withAlpha(BRAND_COLORS.navy, 0.04), borderColor: withAlpha(BRAND_COLORS.navy, 0.15) }}
          data-testid={`${testidPrefix}-other-uncovered`}
        >
          <div className="label-caps mb-1.5">{de ? "Weitere ungedeckte Abwesenheit" : "Other uncovered absence"}</div>
          <ul className="space-y-1">
            {otherUncovered.map((a) => (
              <li
                key={a.id || `${a.from_date}-${a.to_date}`}
                className="text-[13px] text-navy-dark/75 leading-snug"
                data-testid={`${testidPrefix}-other-uncovered-item`}
              >
                {de
                  ? `Ihre Abwesenheit ${deDate(a.from_date)} – ${deDate(a.to_date)} ist noch nicht durch ein Service-Paket gedeckt. Sie können sie separat abdecken.`
                  : `Your absence ${deDate(a.from_date)} – ${deDate(a.to_date)} is not yet covered by a service package. You can cover it separately.`}
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid={`${testidPrefix}-grid`}>
        {PACKAGES.map((p) => {
          const active = selectedId === p.id;
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => selectPackage(p.id)}
              data-testid={`service-package-${p.id}`}
              aria-pressed={active}
              className={`text-left bg-white border p-6 md:p-7 flex flex-col transition-colors ${
                active ? "border-gold ring-1 ring-gold" : "border-navy/10 hover:border-navy/30"
              }`}
            >
              <div className="flex items-center justify-between gap-2 mb-4">
                <div className="flex items-center gap-2">
                  <Package className="w-5 h-5" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.4} />
                  <div className="label-caps">{pkgTitle(p)}</div>
                </div>
                {active && (
                  <span
                    className="w-5 h-5 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: BRAND_COLORS.gold }}
                    data-testid={`service-package-${p.id}-selected`}
                  >
                    <Check className="w-3.5 h-3.5 text-white" strokeWidth={2.5} />
                  </span>
                )}
              </div>
              <h3 className="font-display text-2xl text-navy leading-tight flex-1">
                {pkgMain(p)}
              </h3>
              <div
                className="mt-3 font-display text-xl text-navy"
                data-testid={`service-package-${p.id}-price`}
              >
                {priceLabel(p.id)}
              </div>
              <p className="mt-3 text-[12px] text-navy-dark/50 italic">
                {pkgNote(p)}
              </p>
            </button>
          );
        })}
      </div>

      {selectedPkg && (
        <div className="mt-6 bg-white border border-navy/10 p-6 md:p-7" data-testid={`${testidPrefix}-confirm`}>
          <h4 className="font-display text-xl text-navy mb-2">
            {de ? "Verbindliche Buchung" : "Binding booking"}
          </h4>
          <p className="text-[14px] text-navy-dark leading-relaxed mb-4">
            {de
              ? `Sie buchen hiermit verbindlich „${pkgTitle(selectedPkg)} — ${pkgMain(selectedPkg)}“${multi && propertyName ? ` für „${propertyName}“` : ""}.`
              : `You are bindingly booking “${pkgTitle(selectedPkg)} — ${pkgMain(selectedPkg)}”${multi && propertyName ? ` for “${propertyName}”` : ""}.`}
          </p>

          {/* Ketten-bewusstes Startdatum */}
          {isFollow ? (
            <div
              className="mb-6 flex items-start gap-3 border p-4 rounded"
              style={{ background: withAlpha(BRAND_COLORS.navy, 0.04), borderColor: withAlpha(BRAND_COLORS.navy, 0.15) }}
              data-testid={`${testidPrefix}-seamless-start`}
            >
              <Link2 className="w-5 h-5 mt-0.5 shrink-0" strokeWidth={1.6} style={{ color: BRAND_COLORS.gold }} />
              <div className="flex-1 min-w-0">
                <div className="label-caps mb-1">{de ? "Nahtloser Anschluss" : "Seamless continuation"}</div>
                <p className="text-[13px] text-navy-dark leading-snug">
                  {de
                    ? `Ihr aktuelles Paket läuft bereits. Das neue Paket schließt automatisch lückenlos an — Start am ${deDate(chainInfo.start)}.`
                    : `Your current package is already active. The new package continues seamlessly — starting ${deDate(chainInfo.start)}.`}
                </p>
              </div>
            </div>
          ) : (
            <div className="mb-6" data-testid={`${testidPrefix}-startdate-field`}>
              <label htmlFor="sp-start-date" className="label-caps block mb-2">
                {de ? "Startdatum" : "Start date"}
              </label>
              <input
                id="sp-start-date"
                type="date"
                min={todayStr}
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                data-testid={`${testidPrefix}-start-date`}
                className="w-full md:w-64 border border-navy/20 px-3 py-2.5 text-[15px] text-navy focus:border-gold focus:outline-none"
              />
            </div>
          )}

          {effectiveStart && (() => {
            const end = packageEndDate(selectedId, effectiveStart);
            return (
              <p className="mb-6 -mt-2 text-[13px] text-navy-dark/75" data-testid={`${testidPrefix}-coverage`}>
                {end
                  ? (de ? `Betreuung vom ${deDate(effectiveStart)} bis ${deDate(end)}.` : `Care from ${deDate(effectiveStart)} to ${deDate(end)}.`)
                  : (de ? `Betreuung ab ${deDate(effectiveStart)} · fortlaufend bis auf Widerruf.` : `Care from ${deDate(effectiveStart)} · ongoing until cancelled.`)}
              </p>
            );
          })()}

          <p className="mb-6 text-[14px] text-navy-dark leading-relaxed" data-testid={`${testidPrefix}-pin-hint`}>
            {de ? "Bestätigen Sie die Buchung mit Ihrer PIN." : "Confirm the booking with your PIN."}
          </p>

          {selectionWarning && (
            <div
              className="mb-5 flex items-start gap-3 border p-4 rounded"
              style={{ background: withAlpha(BRAND_COLORS.gold, 0.10), borderColor: BRAND_COLORS.gold }}
              data-testid={`${testidPrefix}-selection-warning`}
              role="alert"
            >
              <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" strokeWidth={1.8} style={{ color: BRAND_COLORS.gold }} />
              <div className="text-[13px] font-medium leading-snug text-navy">{selectionWarning}</div>
            </div>
          )}

          {laterOverlap.length > 0 && (
            <div
              className="mb-5 border p-4 rounded"
              style={{ background: withAlpha(BRAND_COLORS.gold, 0.10), borderColor: BRAND_COLORS.gold }}
              data-testid={`${testidPrefix}-overlap-hint`}
              role="alert"
            >
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" strokeWidth={1.8} style={{ color: BRAND_COLORS.gold }} />
                <div className="flex-1 min-w-0">
                  {replaceOverlap ? (
                    <p className="text-[13px] font-medium leading-snug text-navy" data-testid={`${testidPrefix}-overlap-replacing`}>
                      {de
                        ? `Dieses Paket deckt den gesamten Zeitraum ab und ersetzt Ihr späteres Paket ab ${deDate(laterOverlap[0].start)}.`
                        : `This package covers the entire period and replaces your later package from ${deDate(laterOverlap[0].start)}.`}
                    </p>
                  ) : (
                    <>
                      <p className="text-[13px] font-medium leading-snug text-navy">
                        {de
                          ? `Ihr gewähltes Paket überschneidet sich mit einem bereits gebuchten Paket ab ${deDate(laterOverlap[0].start)}. Wir empfehlen ein Paket über den gesamten Zeitraum – es ersetzt das spätere Paket.`
                          : `Your selected package overlaps a booked package from ${deDate(laterOverlap[0].start)}. We recommend a package covering the entire period – it replaces the later package.`}
                      </p>
                      {fullRangeSuggestion && (
                        <button
                          type="button"
                          onClick={chooseFullRange}
                          data-testid={`${testidPrefix}-overlap-choose-full`}
                          className="mt-3 inline-flex items-center gap-2 text-ivory px-5 py-2.5 text-[11px] tracking-[0.16em] uppercase hover:opacity-90 transition-opacity"
                          style={{ background: BRAND_COLORS.navy }}
                        >
                          {de ? `Gesamtzeitraum-Paket wählen (Service-Paket ${fullRangeSuggestion})` : `Choose full-period package (Service Package ${fullRangeSuggestion})`}
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          <label className="flex items-start gap-3 cursor-pointer mb-6" data-testid={`${testidPrefix}-confirm-label`}>
            <input
              type="checkbox"
              checked={confirmChecked}
              onChange={(e) => setConfirmChecked(e.target.checked)}
              data-testid={`${testidPrefix}-confirm-checkbox`}
              className="mt-0.5 w-4 h-4 accent-[#C9A961]"
            />
            <span className="text-[13px] font-bold text-navy-dark/80 leading-relaxed">
              {de
                ? "Ich bestätige, dass ich dieses Service-Paket verbindlich buchen möchte."
                : "I confirm that I want to book this service package bindingly."}
            </span>
          </label>

          <button
            type="button"
            onClick={startBooking}
            disabled={!confirmChecked || !effectiveStart || submitting || !!selectionWarning || !online || overlapBlocks}
            data-testid={`${testidPrefix}-book-button`}
            className="w-full md:w-auto text-ivory px-6 py-3 text-[11.5px] tracking-[0.18em] uppercase hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ background: BRAND_COLORS.navy }}
          >
            {de ? "Verbindlich buchen" : "Book bindingly"}
          </button>
        </div>
      )}

      <CustomerPinPromptDialog
        open={!!pinMode}
        mode={pinMode === "setup" ? "setup" : "confirm"}
        de={de}
        title={pinMode === "setup" ? undefined : (de ? "Buchung signieren" : "Sign booking")}
        description={
          pinMode === "setup"
            ? undefined
            : (de
                ? "Bitte geben Sie Ihre persönliche 4-stellige PIN ein, um die verbindliche Buchung digital zu signieren."
                : "Please enter your personal 4-digit PIN to sign this binding booking.")
        }
        submitLabel={pinMode === "setup" ? undefined : (de ? "Verbindlich buchen" : "Book bindingly")}
        onClose={() => !submitting && setPinMode(null)}
        onConfirm={submitBooking}
        onSuccess={onPinSetupSuccess}
      />
    </div>
  );
}
